import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { Copy, Plus, X } from 'lucide-react';
import type { DayOfWeek, DayInterval } from '@/client/types.gen';

const DAYS_OF_WEEK: DayOfWeek[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

const DAY_ABBREVIATIONS: Record<DayOfWeek, string> = {
  MONDAY: 'M',
  TUESDAY: 'T',
  WEDNESDAY: 'W',
  THURSDAY: 'T',
  FRIDAY: 'F',
  SATURDAY: 'S',
  SUNDAY: 'S',
};

interface ScheduleEditorProps {
  schedule: Record<DayOfWeek, DayInterval[]>;
  availability: Record<DayOfWeek, boolean>;
  onScheduleChange: (schedule: Record<DayOfWeek, DayInterval[]>) => void;
  onAvailabilityChange: (availability: Record<DayOfWeek, boolean>) => void;
  className?: string;
}

export function ScheduleEditor({
  schedule,
  availability,
  onScheduleChange,
  onAvailabilityChange,
  className,
}: ScheduleEditorProps) {
  const [selectedDayToCopy, setSelectedDayToCopy] = useState<DayOfWeek | null>(
    null
  );

  const getTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const formattedHour = hour.toString().padStart(2, '0');
        const formattedMinute = minute.toString().padStart(2, '0');
        const time = `${formattedHour}:${formattedMinute}`;
        options.push(time);
      }
    }
    return options;
  };

  const timeOptions = getTimeOptions();

  const handleDayToggle = (day: DayOfWeek) => {
    onAvailabilityChange({
      ...availability,
      [day]: !availability[day],
    });
  };

  const handleTimeChange = (
    day: DayOfWeek,
    index: number,
    field: 'start_time' | 'end_time',
    value: string
  ) => {
    const updatedSlots = [...schedule[day]];
    updatedSlots[index] = {
      ...updatedSlots[index],
      [field]: value,
    };

    onScheduleChange({
      ...schedule,
      [day]: updatedSlots,
    });
  };

  const addTimeSlot = (day: DayOfWeek) => {
    const lastSlot = schedule[day][schedule[day].length - 1];
    let newStartTime = '09:00';
    let newEndTime = '17:00';

    if (lastSlot) {
      newStartTime = lastSlot.end_time;
      const [hours, minutes] = lastSlot.end_time.split(':').map(Number);
      const endTimeDate = new Date();
      endTimeDate.setHours(hours, minutes, 0, 0);
      endTimeDate.setHours(endTimeDate.getHours() + 1);
      newEndTime = `${endTimeDate
        .getHours()
        .toString()
        .padStart(2, '0')}:${endTimeDate
        .getMinutes()
        .toString()
        .padStart(2, '0')}`;
    }

    onScheduleChange({
      ...schedule,
      [day]: [
        ...schedule[day],
        { start_time: newStartTime, end_time: newEndTime },
      ],
    });
  };

  const removeTimeSlot = (day: DayOfWeek, index: number) => {
    const updatedSlots = [...schedule[day]];
    updatedSlots.splice(index, 1);

    onScheduleChange({
      ...schedule,
      [day]: updatedSlots,
    });
  };

  const copySchedule = (fromDay: DayOfWeek, toDay: DayOfWeek) => {
    onScheduleChange({
      ...schedule,
      [toDay]: [...schedule[fromDay]],
    });
    onAvailabilityChange({
      ...availability,
      [toDay]: availability[fromDay],
    });
    setSelectedDayToCopy(null);
  };

  return (
    <div className={cn('space-y-2', className)}>
      {DAYS_OF_WEEK.map((day) => (
        <div
          key={day}
          className={cn(
            'rounded-lg border border-border/50 bg-card transition-colors',
            !availability[day] && 'bg-muted/10'
          )}
        >
          <div className="flex items-center px-4 py-3">
            <div className="flex items-center min-w-[180px]">
              <div
                className={cn(
                  'flex items-center justify-center w-8 h-8 rounded-full font-medium text-sm mr-3',
                  availability[day]
                    ? 'bg-primary/10 text-primary'
                    : 'bg-muted/20 text-muted-foreground'
                )}
              >
                {DAY_ABBREVIATIONS[day]}
              </div>
              <span
                className={cn(
                  'font-medium',
                  !availability[day] && 'text-muted-foreground'
                )}
              >
                {day.charAt(0) + day.slice(1).toLowerCase()}
              </span>
            </div>

            {availability[day] ? (
              <div className="flex-1 flex flex-col space-y-2">
                {schedule[day].map((interval, index) => (
                  <div key={index} className="flex items-center">
                    <div className="flex items-center space-x-2">
                      <Select
                        value={interval.start_time}
                        onValueChange={(value) =>
                          handleTimeChange(day, index, 'start_time', value)
                        }
                      >
                        <SelectTrigger className="w-[5.5rem] border-0 bg-transparent h-8 hover:bg-transparent focus:ring-0 focus:ring-offset-0">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {timeOptions.map((time) => (
                            <SelectItem key={`start-${time}`} value={time}>
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <span className="text-muted-foreground/60 text-sm font-light">
                        to
                      </span>

                      <Select
                        value={interval.end_time}
                        onValueChange={(value) =>
                          handleTimeChange(day, index, 'end_time', value)
                        }
                      >
                        <SelectTrigger className="w-[5.5rem] border-0 bg-transparent h-8 hover:bg-transparent focus:ring-0 focus:ring-offset-0">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {timeOptions.map((time) => (
                            <SelectItem key={`end-${time}`} value={time}>
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {schedule[day].length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-muted-foreground/40 hover:text-destructive hover:bg-destructive/10"
                          onClick={() => removeTimeSlot(day, index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => addTimeSlot(day)}
                  className="h-8 px-2 text-primary/60 hover:text-primary hover:bg-primary/10 self-start"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex-1 mx-4">
                <span className="text-sm text-muted-foreground">
                  Unavailable
                </span>
              </div>
            )}

            <div className="flex items-center space-x-2 min-w-[80px] justify-end">
              <Switch
                checked={availability[day]}
                onCheckedChange={() => handleDayToggle(day)}
                className="data-[state=checked]:bg-primary"
              />
              <Popover
                open={selectedDayToCopy === day}
                onOpenChange={(open) => !open && setSelectedDayToCopy(null)}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-primary"
                    onClick={() => setSelectedDayToCopy(day)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-2" align="end">
                  <div className="space-y-1">
                    {DAYS_OF_WEEK.filter((d) => d !== day).map((toDay) => (
                      <Button
                        key={toDay}
                        variant="ghost"
                        className="w-full justify-start text-sm font-normal"
                        onClick={() => copySchedule(day, toDay)}
                      >
                        {toDay.charAt(0) + toDay.slice(1).toLowerCase()}
                      </Button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
